import simplepolygon from 'simplepolygon';

interface Point {
  lat: number;
  lng: number;
}

/* Stack-based Douglas Peucker line simplification routine 
   returned is a reduced GLatLng array 
   After code by  Dr. Gary J. Robinson,
   Environmental Systems Science Centre,
   University of Reading, Reading, UK
*/

function GDouglasPeucker(source: Point[], kink: number): Point[] {
  /* source[] Input coordinates in GLatLngs 	*/
  /* kink	in metres, kinks above this depth kept  */
  /* kink depth is the height of the triangle abc where a-b and b-c are two consecutive line segments */
  let nStack, nDest, start, end, i, sig;
  let devSqr, maxDevSqr, bandSqr;
  let x12, y12, d12, x13, y13, d13, x23, y23, d23;
  const F = (Math.PI / 180.0) * 0.5;
  const indices =
    []; /* array of indices of source points to include in the reduced line */
  const sigStart = []; /* indices of start & end of working section */
  const sigEnd = [];

  /* check for simple cases */

  if ((source?.length || 0) < 3) return source; /* one or two points */

  /* more complex case. initialize stack */

  const nSource = source.length;
  bandSqr = (kink * 360.0) / (2.0 * Math.PI * 6378137.0); /* Now in degrees */
  bandSqr *= bandSqr;
  nDest = 0;
  sigStart[0] = 0;
  sigEnd[0] = nSource - 1;
  nStack = 1;

  /* while the stack is not empty  ... */
  while (nStack > 0) {
    /* ... pop the top-most entries off the stacks */

    start = sigStart[nStack - 1];
    end = sigEnd[nStack - 1];
    nStack--;

    if (end - start > 1) {
      /* any intermediate points ? */

      /* ... yes, so find most deviant intermediate point to
                           either side of line joining start & end points */

      x12 = source[end].lng - source[start].lng;
      y12 = source[end].lat - source[start].lat;
      if (Math.abs(x12) > 180.0) x12 = 360.0 - Math.abs(x12);
      x12 *= Math.cos(
        F * (source[end].lat + source[start].lat)
      ); /* use avg lat to reduce lng */
      d12 = x12 * x12 + y12 * y12;

      for (i = start + 1, sig = start, maxDevSqr = -1.0; i < end; i++) {
        x13 = source[i].lng - source[start].lng;
        y13 = source[i].lat - source[start].lat;
        if (Math.abs(x13) > 180.0) x13 = 360.0 - Math.abs(x13);
        x13 *= Math.cos(F * (source[i].lat + source[start].lat));
        d13 = x13 * x13 + y13 * y13;

        x23 = source[i].lng - source[end].lng;
        y23 = source[i].lat - source[end].lat;
        if (Math.abs(x23) > 180.0) x23 = 360.0 - Math.abs(x23);
        x23 *= Math.cos(F * (source[i].lat + source[end].lat));
        d23 = x23 * x23 + y23 * y23;

        if (d13 >= d12 + d23) devSqr = d23;
        else if (d23 >= d12 + d13) devSqr = d13;
        else devSqr = ((x13 * y12 - y13 * x12) * (x13 * y12 - y13 * x12)) / d12; // solve triangle

        if (devSqr > maxDevSqr) {
          sig = i;
          maxDevSqr = devSqr;
        }
      }

      if (maxDevSqr < bandSqr) {
        /* is there a sig. intermediate point ? */
        /* ... no, so transfer current start point */
        indices[nDest] = start;
        nDest++;
      } else {
        /* ... yes, so push two sub-sections on stack for further processing */
        nStack++;
        sigStart[nStack - 1] = sig;
        sigEnd[nStack - 1] = end;
        nStack++;
        sigStart[nStack - 1] = start;
        sigEnd[nStack - 1] = sig;
      }
    } else {
      /* ... no intermediate points, so transfer current start point */
      indices[nDest] = start;
      nDest++;
    }
  }

  /* transfer last point */
  indices[nDest] = nSource - 1;
  nDest++;

  /* make return array */
  let r = [];
  for (let i = 0; i < nDest; i++) r.push(source[indices[i]]);
  return r;
}

export function processPolygon(polygon: Point[], kink: number): Point[][] {
  const simplifiedPolygon = GDouglasPeucker(polygon, kink);

  if (simplifiedPolygon.length <= 3) {
    return [simplifiedPolygon];
  }

  const multiPolygon = simplepolygon({
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [simplifiedPolygon.map(({ lat, lng }) => [lat, lng])]
    }
  });

  const paths: Point[][] = multiPolygon.features
    .map((feature: { geometry: { coordinates: [[number, number]][] } }) =>
      feature.geometry.coordinates[0]
        .map(([lat, lng]) => ({ lat, lng }))
        .filter(Boolean)
        .slice(0, -1)
    )
    .filter(Boolean);

  return paths;
}
